import { isMatch, Link, useMatches } from '@tanstack/react-router';
import { Fragment, useMemo } from 'react';
import { LuChevronRight } from 'react-icons/lu';

type BreadcrumbItem = {
  label: string;
  href: string;
};

const Breadcrumb = () => {
  const matches = useMatches();

  const breadcrumbItems = useMemo(
    () =>
      matches.flatMap((match) => {
        // Support multiple crumbs per route (for compound path segments like $blastName/optimisations)
        if (isMatch(match, 'loaderData.crumbs') && Array.isArray(match.loaderData?.crumbs)) {
          return match.loaderData.crumbs as BreadcrumbItem[];
        }
        // Support single crumb per route
        if (isMatch(match, 'loaderData.crumb') && match.loaderData?.crumb) {
          return [{ href: match.pathname, label: match.loaderData.crumb as string }];
        }
        return [];
      }),
    [matches],
  );

  if (!breadcrumbItems.length) return null;

  return (
    <nav aria-label="Breadcrumb">
      <ol className="flex shrink-0 items-center gap-2">
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          const sharedClasses = 'flex min-w-0 items-center gap-2 rounded-sm';

          return (
            <Fragment key={`${item.href}-${item.label}`}>
              <li>
                {isLast ? (
                  <span className={`${sharedClasses} select-none text-base`} aria-current="page">
                    {item.label}
                  </span>
                ) : (
                  <Link
                    to={item.href}
                    className={`${sharedClasses} cursor-pointer font-bold text-primary outline-primary outline-offset-4 focus-visible:outline-2`}
                  >
                    {item.label}
                  </Link>
                )}
              </li>
              {!isLast && (
                <li role="presentation" className="shrink-0" aria-hidden>
                  <LuChevronRight className="text-muted-foreground" size={20} />
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
};

export { Breadcrumb };
